/// <reference types="vite/client" />

import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  type IdTokenResult,
  type User,
} from 'firebase/auth';

import { firebaseAuth, handleFirebaseError } from '@/config/firebase';
import type { FirebaseUser, ApiResponse } from '@/types';

export interface AuthState {
  user: FirebaseUser | null;
  isLoading: boolean;
  isOperator: boolean;
  error: string | null;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

class AuthService {
  private authStateListeners: ((state: AuthState) => void)[] = [];
  private currentState: AuthState = {
    user: null,
    isLoading: true,
    isOperator: false,
    error: null,
  };

  constructor() {
    this.initializeAuth();
  }

  /**
   * Initialize Firebase Auth state listener
   */
  private initializeAuth(): void {
    if (!firebaseAuth) {
      console.error('Firebase Auth not initialized');
      this.updateState({
        ...this.currentState,
        isLoading: false,
        error: 'Authentication service not available',
      });
      return;
    }

    onAuthStateChanged(firebaseAuth, async (user: User | null) => {
      if (user) {
        try {
          // Get ID token with custom claims
          const tokenResult = await user.getIdTokenResult();
          const isOperator = this.validateOperatorClaims(tokenResult);

          const firebaseUser: FirebaseUser = {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
            emailVerified: user.emailVerified,
            customClaims: tokenResult.claims as { isOperator?: boolean; role?: string; },
          };

          this.updateState({
            user: firebaseUser,
            isLoading: false,
            isOperator,
            error: null,
          });
        } catch (error) {
          console.error('Failed to process auth state:', error);
          this.updateState({
            user: null,
            isLoading: false,
            isOperator: false,
            error: handleFirebaseError(error),
          });
        }
      } else {
        this.updateState({
          user: null,
          isLoading: false,
          isOperator: false,
          error: null,
        });
      }
    });
  }

  /**
   * Validate operator custom claims
   */
  private validateOperatorClaims(tokenResult: IdTokenResult): boolean {
    const { claims } = tokenResult;

    // In development mode, skip operator check for testing
    if (import.meta.env.VITE_SKIP_OPERATOR_CHECK === 'true') {
      console.log('🔧 DEV MODE: Skipping operator claims validation');
      return true;
    }

    // Check if user has operator claims
    if (!claims.isOperator) {
      console.warn('User does not have operator claims');
      return false;
    }

    // Additional validation for production
    if (import.meta.env.PROD) {
      // Check token expiration
      const expirationTime = new Date(tokenResult.expirationTime).getTime();
      const now = Date.now();

      if (expirationTime <= now) {
        console.warn('Operator token has expired');
        return false;
      }

      // Check if token was issued recently (security measure)
      const issuedAtTime = new Date(tokenResult.issuedAtTime).getTime();
      const tokenAge = now - issuedAtTime;
      const maxTokenAge = 24 * 60 * 60 * 1000; // 24 hours

      if (tokenAge > maxTokenAge) {
        console.warn('Operator token is too old, re-authentication required');
        return false;
      }
    }

    return true;
  }

  /**
   * Sign in with email and password
   */
  async signIn(credentials: LoginCredentials): Promise<ApiResponse<FirebaseUser>> {
    if (!firebaseAuth) {
      return {
        success: false,
        error: {
          code: 500,
          message: 'Authentication service not available',
        },
        metadata: {
          timestamp: new Date().toISOString(),
          version: '1.0',
        },
      };
    }

    try {
      this.updateState({
        ...this.currentState,
        isLoading: true,
        error: null,
      });

      // Validate credentials
      if (!credentials.email || !credentials.password) {
        throw new Error('Email and password are required');
      }

      if (!credentials.email.includes('@')) {
        throw new Error('Please enter a valid email address');
      }

      // Attempt sign in
      const userCredential = await signInWithEmailAndPassword(
        firebaseAuth,
        credentials.email,
        credentials.password
      );

      // Get ID token with custom claims
      const tokenResult = await userCredential.user.getIdTokenResult();
      const isOperator = this.validateOperatorClaims(tokenResult);

      if (!isOperator) {
        // Sign out if not an operator
        await firebaseSignOut(firebaseAuth);
        throw new Error('Access denied. Operator privileges required.');
      }

      const firebaseUser: FirebaseUser = {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        displayName: userCredential.user.displayName,
        photoURL: userCredential.user.photoURL,
        emailVerified: userCredential.user.emailVerified,
        customClaims: tokenResult.claims as { isOperator?: boolean; role?: string; },
      };

      // Log successful authentication for audit
      this.logAuthEvent('login_success', firebaseUser.uid, {
        email: firebaseUser.email,
        timestamp: new Date().toISOString(),
      });

      return {
        success: true,
        data: firebaseUser,
        message: 'Successfully signed in',
        metadata: {
          timestamp: new Date().toISOString(),
          version: '1.0',
        },
      };
    } catch (error) {
      const errorMessage = handleFirebaseError(error);
      
      // Log failed authentication attempt
      this.logAuthEvent('login_failure', null, {
        email: credentials.email,
        error: errorMessage,
        timestamp: new Date().toISOString(),
      });

      this.updateState({
        ...this.currentState,
        isLoading: false,
        error: errorMessage,
      });

      return {
        success: false,
        error: {
          code: 401,
          message: errorMessage,
        },
        metadata: {
          timestamp: new Date().toISOString(),
          version: '1.0',
        },
      };
    }
  }

  /**
   * Sign out current user
   */
  async signOut(): Promise<ApiResponse<void>> {
    if (!firebaseAuth) {
      return {
        success: false,
        error: {
          code: 500,
          message: 'Authentication service not available',
        },
        metadata: {
          timestamp: new Date().toISOString(),
          version: '1.0',
        },
      };
    }

    try {
      const currentUser = this.currentState.user;
      
      // Log logout for audit
      if (currentUser) {
        this.logAuthEvent('logout', currentUser.uid, {
          email: currentUser.email,
          timestamp: new Date().toISOString(),
        });
      }

      await firebaseSignOut(firebaseAuth);

      return {
        success: true,
        message: 'Successfully signed out',
        metadata: {
          timestamp: new Date().toISOString(),
          version: '1.0',
        },
      };
    } catch (error) {
      const errorMessage = handleFirebaseError(error);
      
      return {
        success: false,
        error: {
          code: 500,
          message: errorMessage,
        },
        metadata: {
          timestamp: new Date().toISOString(),
          version: '1.0',
        },
      };
    }
  }

  /**
   * Get current authentication state
   */
  getAuthState(): AuthState {
    return { ...this.currentState };
  }

  /**
   * Subscribe to authentication state changes
   */
  onAuthStateChange(callback: (state: AuthState) => void): () => void {
    this.authStateListeners.push(callback);
    
    // Immediately call with current state
    callback(this.currentState);
    
    // Return unsubscribe function
    return () => {
      const index = this.authStateListeners.indexOf(callback);
      if (index > -1) {
        this.authStateListeners.splice(index, 1);
      }
    };
  }

  /**
   * Refresh operator token
   */
  async refreshToken(): Promise<boolean> {
    if (!firebaseAuth?.currentUser) {
      return false;
    }

    try {
      const tokenResult = await firebaseAuth.currentUser.getIdTokenResult(true);
      const isOperator = this.validateOperatorClaims(tokenResult);

      if (!isOperator) {
        await this.signOut();
        return false;
      }

      return true;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      return false;
    }
  }

  /**
   * Check if current session is valid
   */
  async validateSession(): Promise<boolean> {
    const { user, isOperator } = this.currentState;
    
    if (!user || !isOperator) {
      return false;
    }

    // Refresh token to ensure it's still valid
    return this.refreshToken();
  }

  /**
   * Update authentication state and notify listeners
   */
  private updateState(newState: AuthState): void {
    this.currentState = { ...newState };
    this.authStateListeners.forEach(callback => callback(this.currentState));
  }

  /**
   * Log authentication events for audit trail
   */
  private logAuthEvent(
    event: string,
    userId: string | null,
    details: Record<string, unknown>
  ): void {
    // In production, this should send to audit logging service
    const auditLog = {
      event,
      userId,
      details,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    };

    if (import.meta.env.DEV) {
      console.info('Auth Event:', auditLog);
    }

    // Send to monitoring service in production
    if (import.meta.env.PROD && typeof window !== 'undefined' && 'Sentry' in window) {
      (window as any).Sentry?.addBreadcrumb({
        category: 'auth',
        message: event,
        level: 'info',
        data: auditLog,
      });
    }
  }
}

// Export singleton instance
export const authService = new AuthService();

// Export utility functions

/**
 * NOTE: The `FirebaseUser` object returned by `getCurrentUser()` is a lightweight 
 * Data Transfer Object (DTO) and does not contain Firebase SDK methods like `getIdToken()`.
 * To get the authentication token, use the `getAuthIdToken()` helper function below.
 */
export const getCurrentUser = (): FirebaseUser | null => {
  return authService.getAuthState().user;
};

/**
 * Retrieves the Firebase ID token for the current user.
 * Throws an error if the user is not authenticated.
 * @param forceRefresh - If true, forces a token refresh.
 * @returns A promise that resolves with the ID token string.
 */
export async function getAuthIdToken(forceRefresh = false): Promise<string> {
  if (!firebaseAuth?.currentUser) {
    throw new Error('User not authenticated. Cannot get ID token.');
  }
  return firebaseAuth.currentUser.getIdToken(forceRefresh);
}

export const isAuthenticated = (): boolean => {
  const { user, isOperator } = authService.getAuthState();
  return !!(user && isOperator);
};

export const requireAuth = (): FirebaseUser => {
  const { user, isOperator } = authService.getAuthState();
  
  if (!user || !isOperator) {
    throw new Error('Authentication required');
  }
  
  return user;
};
